import { useCallback, useState } from 'react';

// Width of the Sissi AI panel, remembered per browser. The page (main
// container) takes the rest and never drops below MAIN_MIN_WIDTH while
// dragging.

export const PANEL_LIMITS = { min: 340, max: 560, initial: 400 } as const;
export const MAIN_MIN_WIDTH = 640;
// Below this viewport the panel starts at its minimum, unless the clinician
// chose a width.
export const NARROW_BELOW = 1440;
const NARROW_WIDTH = 360;

const KEY = 'clinic-agent-panel-width';

export const clampWidth = (value: number) =>
  Math.round(Math.min(PANEL_LIMITS.max, Math.max(PANEL_LIMITS.min, value)));

function read(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

function write(value: string) {
  try {
    localStorage.setItem(KEY, value);
  } catch {
    /* not remembered this time */
  }
}

function initialWidth(): number {
  const stored = Number(read());
  if (stored) return clampWidth(stored);
  const narrow = typeof window !== 'undefined' && window.matchMedia(`(max-width: ${NARROW_BELOW - 1}px)`).matches;
  return narrow ? NARROW_WIDTH : PANEL_LIMITS.initial;
}

export function usePanelWidth() {
  const [width, setWidth] = useState<number>(initialWidth);

  // While dragging: update on screen only.
  const preview = useCallback((value: number) => setWidth(value), []);

  // At the end of a drag or a key press: remember it.
  const commit = useCallback((value: number) => {
    setWidth(value);
    write(String(value));
  }, []);

  return { width, preview, commit };
}
